<template>
  <section>
    <div class="content-wrapper">
      <errors v-if="isVisibleErrorsModal"></errors>
      <question v-if="display"></question>
      <div class="content-heading">
        All users
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <breadcrumb class="breadcrumb" v-bind:breadcrumbsData="breadcrumbData" />
      <br />

      <div class="row">
        <spinner v-if="loading"></spinner>
        <div class="col-lg-12">
          <div id="panelChart9" class="panel panel-default">
            <div class="panel-title">Add Funds request</div>
            <div class="line-separator"></div>
            <div class="bottom-margin">
              <div class="modal-body mt-lg">
                <div class="row top-margin">
                  <div class="col-md-6">
                    <label>Enter investment amount <strong class="text-info">*</strong></label>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <input v-on:change="removeNegative"
                        v-money="money"
                        v-model="investmentAmount"
                        autocomplete="off"
                        name="investmentAmount"
                        id="investmentAmount"
                        :class="{ 'form-control text-right input-md': true, 'form-control-error': isInvestmentAmountInvalid }"
                        v-validate="{required: true}"
                        v-on:blur="validateInvestmentAmount()">
                      <span v-if="isInvestmentAmountInvalid" class="error-feedback">{{investmentAmountError}}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row top-margin">
                <div class="col-md-12">
                  <span>Please append your comment below *</span>
                  <restricted-textarea
                    :input="comment"
                    :limit="255"
                    id="comment"
                    placeholder=""
                    @inputUpdated="commentUpdated"
                    :required="true"
                  />
                  <p
                    class="top-margin"
                    style="color: red;"
                    v-if="!comment || information.comment === comment"
                  >
                    <i>You must enter or append a comment.</i>
                  </p>
                </div>
              </div>
              <div class="row top-margin">
                <div class="col-md-10">
                  <button
                    type="submit"
                    class="btn btn-info btn-lg button-white buttons"
                    v-on:click="information.isNewRequest ? goToNoteDetail() : showCancelConfirmationModal()"
                    v-bind:disabled="!information.isNewRequest && isCancelButtonDisabled()"
                  >Cancel request</button>
                  <button
                    type="submit"
                    class="btn btn-info btn-lg button-white buttons"
                    v-on:click="submitApproveAddFunds"
                    v-bind:disabled="information.isNewRequest || isButtonDisabled()"
                  >Approve Add Funds</button>
                  <button
                    type="submit"
                    class="btn btn-info btn-lg button-approve buttons"
                    v-on:click="submitRequest"
                    v-bind:disabled="!information.isNewRequest || isButtonDisabled()"
                  >Submit request</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';

import {
  HIDE_QUESTION_MODAL,
  SET_ERRORS_MODAL_INFO,
  SHOW_ERRORS_MODAL,
  SHOW_QUESTION_MODAL,
} from '@/store/mutation-types';

import {
  VMoney,
} from 'v-money';

import breadcrumb from '@/components/Breadcrumb';
import Errors from '@/components/modals/Errors';
import Question from '@/components/modals/Question';
import spinner from '@/components/widgets/Spinner';
import { Constants } from '@/data/static/constants';
import { getAccounTypeProperties, formatAmount, formatDecimal } from '@/util';
import { UserTypes } from '@/data/static/userTypes';
import RestrictedTextArea from '@/components/formControls/RestrictedTextArea';


const mix = {
  methods: {
    formatAmount,
    formatDecimal,
  },
};

export default {
  name: 'addFunds',
  beforeMount() {
    this.setParams();
    this.loadInformation();
    this.loadInvestmentsValidations();
  },
  components: {
    breadcrumb,
    spinner,
    errors: Errors,
    question: Question,
    'restricted-textarea': RestrictedTextArea,
  },
  mixins: [mix],
  directives: {
    money: VMoney,
  },
  watch: {
    information: function changeNote(newTransferNote) {
      const {
        accountName,
        accountType,
        id: accountId,
        users,
      } = newTransferNote.account;
      this.breadcrumbData = [];
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            title: 'Search',
            goto: { name: 'Users' },
            selected: false,
          },
        ),
      );
      const params = Object.assign({}, this.$route.params);
      // prettier-ignore
      const user = users.find(search => search.id === Number(params.userId));
      const { userType, firstName, lastName } = user;
      let goto;
      if (userType === UserTypes.FINANCIAL_ADVISOR.name) {
        goto = {
          name: 'EditFinancialAdvisor',
          params: {
            name: params.name,
            userId: params.userId,
          },
        };
      } else {
        goto = {
          name: 'UserDetail',
          params: { name: params.name, userId: params.userId },
        };
      }
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            goto,
            title: firstName.concat(' ', lastName),
            selected: false,
          },
        ),
      );
      const accountTypeProperties = getAccounTypeProperties(accountType);
      const gotoAccount = {
        name: accountTypeProperties.component,
        params: {
          accountId,
          name: params.name,
          userId: params.userId,
        },
      };
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            title: accountName,
            goto: gotoAccount,
            selected: false,
          },
        ),
      );
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            title: 'Note'.concat(' ', newTransferNote.noteId),
            goto: { name: 'NoteDetail', params: this.$route.params },
            selected: false,
          },
        ),
      );
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            title: 'Transfer',
            goto: { name: this.$route.name, params: this.$route.params },
            selected: true,
          },
        ),
      );
    },
  },
  data() {
    return {
      noteId: '',
      userId: '',
      name: '',
      breadcrumbData: [],
      loading: false,
      information: {},
      comment: '',
      investmentAmount: 0,
      isInvestmentAmountInvalid: false,
      minimumInvestmentAmount: null,
      money: {
        decimal: '.',
        thousands: ',',
        prefix: '$',
        precision: 2,
      },
    };
  },
  computed: {
    ...mapState('errors', ['isVisibleErrorsModal']),
    ...mapState('question', ['display']),
    ...mapState('app',
    ['investmentsRestrictions'],
    ),
  },
  methods: {
    commentUpdated($event) {
      this.comment = $event;
    },
    ...mapActions('note', [
      'getAdditionalDepositInformation',
      'requestAddFunds',
      'approveAddFunds',
      'cancelNoteAddFunds',
    ]),
    ...mapMutations('question', {
      hideQuestionModal: HIDE_QUESTION_MODAL,
      showQuestionModal: SHOW_QUESTION_MODAL,
    }),
    ...mapMutations('errors', {
      showErrorsModal: SHOW_ERRORS_MODAL,
      setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
    }),
    loadInvestmentsValidations() {
      this.minimumInvestmentAmount = this.investmentsRestrictions.minimumAmount;
      this.investmentAmountError = `Please invest at least ${formatAmount(parseInt(this.minimumInvestmentAmount, 10))} in order to proceed`;
    },
    getUnformattedValue(value) {
      const unformattedValue = value.toString().replace('$', '').replace(/,/g, '');
      return this.formatDecimal(unformattedValue);
    },
    removeNegative() {
      this.investmentAmount = this.investmentAmount.toString().replace('-', '');
    },
    validateInvestmentAmount() {
      const investmentAmountValue = this.getUnformattedValue(this.investmentAmount);
      if (investmentAmountValue < this.minimumInvestmentAmount) {
        this.isInvestmentAmountInvalid = true;
      } else {
        this.isInvestmentAmountInvalid = false;
      }
    },
    setParams() {
      this.name = this.$route.params.name;
      this.userId = this.$route.params.userId;
      this.noteId = this.$route.params.noteId;
    },
    async loadInformation() {
      this.loading = true;
      try {
        const response = await this.getAdditionalDepositInformation(this.noteId);
        this.information = response;
        if (!this.information.isNewRequest) {
          this.comment = this.information.comment;
          this.investmentAmount = formatDecimal(this.information.amount).toFixed(2);
        }
        this.loading = false;
      } catch (err) {
        this.loading = false;
        if (err !== 'UNAUTHORIZED') {
          this.setErrorsModalInfo([
            'We could not retrieve the transfer note details.',
          ]);
          this.showErrorsModal();
        }
      }
    },
    isCancelButtonDisabled() {
      if (!this.comment) {
        return true;
      }
      if (this.comment === this.information.comment) {
        return true;
      }
      return false;
    },
    isButtonDisabled() {
      if (!this.comment) {
        return true;
      }
      if (this.comment === this.information.comment) {
        return true;
      }
      if (!this.investmentAmount) {
        return true;
      }
      return this.isInvestmentAmountInvalid;
    },
    async submitRequest() {
      this.loading = true;
      const request = {
        comment: this.comment,
        amount: this.getUnformattedValue(this.investmentAmount).toFixed(2),
      };
      try {
        await this.requestAddFunds({
          noteId: this.noteId,
          request,
        });
        this.loading = false;
        this.goToNoteDetail();
      } catch (err) {
        this.loading = false;
        this.setErrorsModalInfo([
          'There was an error when submitting a request.',
        ]);
        this.showErrorsModal();
      }
    },
    async submitApproveAddFunds() {
      this.loading = true;
      const request = {
        comment: this.comment,
        amount: this.getUnformattedValue(this.investmentAmount).toFixed(2),
      };
      try {
        await this.approveAddFunds({
          noteId: this.noteId,
          request,
        });
        this.loading = false;
        this.goToNoteDetail();
      } catch (err) {
        this.loading = false;
        this.setErrorsModalInfo([
          'There was an error when approving a request.',
        ]);
        this.showErrorsModal();
      }
    },
    showCancelConfirmationModal() {
      this.showQuestionModal({
        title: Constants.addFunds.modalTitle,
        question: Constants.addFunds.modalQuestion,
        method: async () => {
          const request = {
            comment: this.comment,
          };
          this.loading = true;
          try {
            await this.cancelNoteAddFunds({
              noteId: this.noteId,
              request,
            });
            this.loading = false;
            this.hideQuestionModal();
            this.goToNoteDetail();
          } catch (err) {
            this.loading = false;
            this.setErrorsModalInfo([
              'There was an error when canceling a request.',
            ]);
            this.showErrorsModal();
            this.hideQuestionModal();
          }
        },
        yesButtonText: Constants.addFunds.modalYesButtonText,
        hideCancelButton: false,
      });
    },
    goToNoteDetail() {
      this.$router.push({
        name: 'NoteDetail',
        params: {
          name: this.name,
          noteId: this.noteId,
          userId: this.userId,
        },
      });
    },
  },
};
</script>

<style scoped>
.line-separator {
  padding: 8px;
  line-height: 1.52857143;
  vertical-align: top;
  border-top: 1px solid #eee;
}
.top-margin {
  margin-top: 3%;
}
.bottom-margin {
  margin-bottom: 1%;
}
.space {
  margin-top: 4%;
}
.content-heading {
  font-weight: normal;
}
.panel-title {
  font-size: 20px;
}
.panel-default {
  padding: 30px 40px;
  width: 100%;
}
label {
  color: #888;
  font-size: 14px;
  width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.button-approve {
  background-color: #127285;
}
.button-white {
  background-color: white;
  border-color: #127285;
  color: #127285;
}
.buttons {
  display: inline-block;
  margin: 5px;
}
.searchbox {
  display: flex;
  flex-direction: row;
}
.searchbox input {
  height: 39px;
  flex: 1;
  border-radius: 4px 0 0 4px;
}
.searchbox .btn-default {
  text-align: center;
  border-radius: 0 4px 4px 0;
}
.green-text {
  color: #558c1d;
  font-weight: 800;
}
.action-span {
  display: block;
  height: 32px;
  min-width: 113px;
  padding: 5px 0;
}
.check {
  margin-right: 5px;
}
.not-found-alert {
  margin-top: -36px !important;
  margin-bottom: 0px;
}
</style>



// WEBPACK FOOTER //
// AddFunds.vue?20df8c5e